import { Box, Stack } from 'braid-design-system';
import findIndex from 'lodash/findIndex';
import type { ComponentProps } from 'react';
import { ModifyMySearchAccessabilityLink } from 'src/components/SearchResultPage/SplitView/AccessabilityLinks/AccessabilityLinks';
import { useSelector } from 'src/store/react';
import {
  canShowNudges,
  selectCanonicalCompany,
  selectClearCompanyRefinements,
  selectIsRadialFilterNudgeShown,
  selectNudges,
  selectZoneFeatures,
} from 'src/store/selectors';

import type { SearchResultJob } from '@seek/chalice-types';

import JobListItem from './JobListItem/JobListItem';
import DistanceFromNudge from './Nudges/DistanceFromNudge/DistanceFromNudge';
import ProfileNudge from './Nudges/ProfileNudge/ProfileNudge';
import CompanySummary from '../CompanySummary/CompanySummary';
import CompanyResults from '../CompanyResults/CompanyResults';

interface SearchResultListProps
  extends Pick<ComponentProps<typeof JobListItem>, 'setCurrentJob'> {
  jobs: SearchResultJob[];
  isCompanySearch: boolean;
  selectedJobId?: number;
}

const getIndexOfFirstNonPremium = (jobs: SearchResultJob[]): number =>
  findIndex(jobs, ['isPremium', false]);

const SearchResultList = ({
  jobs = [],
  setCurrentJob,
  selectedJobId,
  isCompanySearch,
}: SearchResultListProps) => {
  const { NUDGE_ENABLED } = useSelector(selectZoneFeatures);
  const canShowNudge = useSelector(canShowNudges);
  const shouldShowProfileNudge = useSelector(selectNudges).length > 0;
  const shouldShowDistanceFromNudge = useSelector(
    selectIsRadialFilterNudgeShown,
  );
  const canonicalCompany = useSelector(selectCanonicalCompany);
  const clearCompanyRefinements =
    useSelector(selectClearCompanyRefinements) || false;
  const companyName = canonicalCompany?.description;

  const profileNudgePosition = getIndexOfFirstNonPremium(jobs) + 1;

  const jobList = jobs.map((job) => (
    <JobListItem
      key={`${job.id}-${job.displayType}`}
      job={job}
      solMetadata={job.solMetadata}
      setCurrentJob={setCurrentJob}
      isSelected={selectedJobId === job.id}
    />
  ));

  const distanceFromNudgePosition = Math.floor(jobList.length / 2);

  if (NUDGE_ENABLED && shouldShowProfileNudge && canShowNudge) {
    jobList.splice(
      profileNudgePosition,
      0,
      <ProfileNudge key="ProfileNudge" position={profileNudgePosition} />,
    );
  }

  if (shouldShowDistanceFromNudge) {
    jobList.splice(
      distanceFromNudgePosition,
      0,
      <DistanceFromNudge
        key="DistanceFromNudge"
        position={distanceFromNudgePosition}
      />,
    );
  }

  return (
    <Box>
      {isCompanySearch && companyName ? (
        <Box paddingBottom="medium">
          <Stack space="small">
            <CompanySummary companyName={companyName} />
            {typeof clearCompanyRefinements === 'object' ? (
              <CompanyResults
                isCompanySearch={isCompanySearch}
                clearCompanyRefinements={clearCompanyRefinements}
                jobs={jobs || []}
              />
            ) : null}
          </Stack>
        </Box>
      ) : null}

      <ModifyMySearchAccessabilityLink placement="job-list" position="top" />

      <Stack space="xsmall">{jobList}</Stack>

      <ModifyMySearchAccessabilityLink placement="job-list" position="bottom" />
    </Box>
  );
};

export default SearchResultList;
